import { useCallback, useMemo } from 'react';

import { Box as Flex, MenuItem, Tooltip } from '@material-ui/core';

import { RiDeleteBinLine, RiRefreshLine } from '@remixicon/react';
import { Editor } from '@tiptap/core';

import { AvailableMetric } from '@spotify/confidence-editor-react';
import { BlockReorderButtons } from '@spotify/confidence-editor-react';
import { BlockCommentButton } from '@spotify/confidence-editor-react';
import { ToolbarButton, ToolbarCard } from '@spotify/confidence-editor-react';
import { ToolbarSelect } from '@spotify/confidence-editor-react';
import { closeAllDropdowns } from '@spotify/confidence-editor-react';
import { CommentZoneProvider } from '@spotify/plugin-confidence-comments-react';

type MetricCardToolbarProps = {
  editor: Editor;
  metricId: string | null;
  metrics: AvailableMetric[] | undefined;
  zoneId?: string;
  onSelect: (metricId: string) => void;
  onDelete: () => void;
  onToolbarActiveChange: (active: boolean) => void;
  getPos: () => number | undefined;
  isStale?: boolean;
};

export const MetricCardToolbar = ({
  editor,
  metricId,
  metrics,
  zoneId,
  onSelect,
  onDelete,
  onToolbarActiveChange,
  getPos,
  isStale,
}: MetricCardToolbarProps) => {
  const handleOpenChange = useCallback(
    (open: boolean) => {
      onToolbarActiveChange(open);
      if (open) {
        closeAllDropdowns();
        const pos = getPos();
        if (typeof pos === 'number') {
          editor.commands.setNodeSelection(pos);
        }
      }
    },
    [editor, getPos, onToolbarActiveChange],
  );

  const selectOptions = useMemo(
    () =>
      (metrics ?? []).map(metric => ({
        label: metric.name,
        value: metric.id,
        description:
          metric.type === 'guardrail' ? 'Guardrail metric' : 'Success metric',
      })),
    [metrics],
  );

  const handleRefresh = useCallback(() => {
    if (metricId) {
      onSelect(metricId);
    }
  }, [metricId, onSelect]);

  return (
    <ToolbarCard>
      <Flex
        display="flex"
        alignItems="center"
        style={{
          gap: 'var(--bui-space-0_5)',
        }}
      >
        <BlockReorderButtons getPos={getPos} editor={editor} />
        <ToolbarSelect
          aria-label="Select metric"
          name="metric-card-hover-select"
          value={metricId || ''}
          onChange={e => {
            const newValue = e.target.value;
            if (!newValue) return;
            onSelect(String(newValue));
          }}
          onOpen={() => handleOpenChange(true)}
          onClose={() => handleOpenChange(false)}
          disabled={(metrics ?? []).length === 0}
        >
          {selectOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </ToolbarSelect>
        {isStale ? (
          <Tooltip title="Update with latest values">
            <ToolbarButton
              aria-label="Update with latest values"
              onClick={handleRefresh}
            >
              <RiRefreshLine color="var(--bui-fg-warning)" />
            </ToolbarButton>
          </Tooltip>
        ) : undefined}
        {zoneId && (
          <CommentZoneProvider zone={zoneId}>
            <BlockCommentButton />
          </CommentZoneProvider>
        )}
        <Tooltip title="Delete metric card">
          <ToolbarButton aria-label="Delete metric card" onClick={onDelete}>
            <RiDeleteBinLine />
          </ToolbarButton>
        </Tooltip>
      </Flex>
    </ToolbarCard>
  );
};
